import React, { useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import Button from '../ui/Button';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
  price: number;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  planId,
  planName,
  price
}) => {
  const { subscribe, addPaymentMethod, paymentMethods, isLoading } = useSubscription();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: 'alipay' as 'alipay' | 'wechat' | 'card',
    isDefault: true
  });

  if (!isOpen) return null;

  const handleSubscribe = async () => {
    try {
      if (!selectedPaymentMethod && !showAddPayment) {
        alert('请选择支付方式');
        return;
      }

      let paymentMethodId = selectedPaymentMethod;

      if (showAddPayment) {
        await addPaymentMethod(newPaymentMethod);
        paymentMethodId = 'pm_' + Date.now(); // 模拟新支付方式ID
      }

      await subscribe(planId, paymentMethodId);
      alert(`成功订阅${planName}！`);
      onClose();
    } catch (error) {
      alert('订阅失败，请重试');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">订阅 {planName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-6">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            ¥{price}<span className="text-sm text-gray-600">/月</span>
          </div>
          <p className="text-gray-600">
            您将被收取 ¥{price} 的月费，可随时取消
          </p>
        </div>

        {!showAddPayment ? (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">选择支付方式</h3>

            {paymentMethods.length > 0 ? (
              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={selectedPaymentMethod === method.id}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2">
                          {method.type === 'alipay' ? '💰' :
                           method.type === 'wechat' ? '💬' : '💳'}
                        </span>
                        <span className="capitalize">
                          {method.type === 'alipay' ? '支付宝' :
                           method.type === 'wechat' ? '微信支付' : '银行卡'}
                        </span>
                        {method.isDefault && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            默认
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-4">暂无支付方式</p>
            )}

            <button
              onClick={() => setShowAddPayment(true)}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              + 添加支付方式
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">添加支付方式</h3>

            <div className="space-y-3">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="newPaymentType"
                  value="alipay"
                  checked={newPaymentMethod.type === 'alipay'}
                  onChange={(e) => setNewPaymentMethod({
                    ...newPaymentMethod,
                    type: e.target.value as 'alipay'
                  })}
                  className="mr-3"
                />
                <span className="mr-2">💰</span>
                <span>支付宝</span>
              </label>

              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="newPaymentType"
                  value="wechat"
                  checked={newPaymentMethod.type === 'wechat'}
                  onChange={(e) => setNewPaymentMethod({
                    ...newPaymentMethod,
                    type: e.target.value as 'wechat'
                  })}
                  className="mr-3"
                />
                <span className="mr-2">💬</span>
                <span>微信支付</span>
              </label>

              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="newPaymentType"
                  value="card"
                  checked={newPaymentMethod.type === 'card'}
                  onChange={(e) => setNewPaymentMethod({
                    ...newPaymentMethod,
                    type: e.target.value as 'card'
                  })}
                  className="mr-3"
                />
                <span className="mr-2">💳</span>
                <span>银行卡</span>
              </label>
            </div>

            <button
              onClick={() => setShowAddPayment(false)}
              className="text-gray-600 hover:text-gray-700 mt-3"
            >
              返回选择已有支付方式
            </button>
          </div>
        )}

        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            取消
          </Button>
          <Button
            onClick={handleSubscribe}
            disabled={isLoading || (!selectedPaymentMethod && !showAddPayment)}
            className="flex-1"
          >
            {isLoading ? '处理中...' : `支付 ¥${price}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;